/**
 * Gestionnaire de thèmes
 * Bascule entre les thèmes via l'attribut data-theme sur <html>
 * Tous les CSS des thèmes sont chargés statiquement dans main.js
 */

import { THEMES, DEFAULT_THEME } from '../config/theme-config.js';

// Clé localStorage dynamique basée sur la version (classic ou admin)
const STORAGE_KEY = `qcm-current-theme-${window.theme_prefix || 'classic'}`;

// Module du thème actuellement chargé (pour la logique JS)
let currentThemeModule = null;
let currentThemeName = null;

/**
 * Change le thème actif
 * @param {string} themeName - Nom du thème à activer
 * @returns {Promise<void>}
 */
export async function setTheme(themeName) {
  // Vérifier que le thème existe
  if (!THEMES[themeName]) {
    console.error(`❌ Thème "${themeName}" introuvable`);
    console.log('Thèmes disponibles:', Object.keys(THEMES).join(', '));
    return;
  }

  // Si c'est déjà le thème actif, ne rien faire
  if (currentThemeName === themeName) {
    console.log(`Le thème "${themeName}" est déjà actif`);
    return;
  }

  console.log(`Application du thème ${currentThemeName || ''} → ${themeName}`);

  // 1. Cleanup du thème précédent (logique JS uniquement)
  if (currentThemeModule && typeof currentThemeModule.cleanup === 'function') {
    try {
      await currentThemeModule.cleanup();
    } catch (error) {
      console.error('Erreur lors du cleanup du thème:', error);
    }
  }

  // 2. Changer l'attribut data-theme (switch CSS instantané)
  document.documentElement.setAttribute('data-theme', themeName);

  // 3. Charger le module JS du thème (pour init/cleanup)
  try {
    const theme = THEMES[themeName];
    currentThemeModule = await theme.module();
    currentThemeName = themeName;

    // 4. Initialiser le nouveau thème (logique JS)
    if (typeof currentThemeModule.init === 'function') {
      await currentThemeModule.init();
    }

    // 5. Sauvegarder dans localStorage
    localStorage.setItem(STORAGE_KEY, themeName);

    console.log(`Thème "${themeName}" activé`);
  } catch (error) {
    console.error(`❌ Erreur lors du chargement du thème "${themeName}":`, error);
    currentThemeName = null;
    currentThemeModule = null;
  }
}

/**
 * Récupère le nom du thème actuellement actif
 * @returns {string|null}
 */
export function getCurrentTheme() {
  return currentThemeName;
}

/**
 * Liste tous les thèmes disponibles
 * @returns {Object}
 */
export function listThemes() {
  return Object.entries(THEMES).map(([id, config]) => ({
    id,
    name: config.name,
    description: config.description,
    active: id === currentThemeName
  }));
}

/**
 * Initialise le thème au démarrage
 * Charge le thème sauvegardé ou le thème par défaut
 */
export async function initTheme() {
  //console.log('🎨 Initialisation du système de thèmes');

  // Vérifier si un thème est sauvegardé
  const savedTheme = localStorage.getItem(STORAGE_KEY);
  const themeToLoad = (savedTheme && THEMES[savedTheme]) ? savedTheme : DEFAULT_THEME;

  // Activer le thème
  await setTheme(themeToLoad);
}
